import React, { useEffect, useState, useLayoutEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Table, Input, Button, Row, Col, Spinner, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import useFbLeads from "../../Hooks/useFbLeads";
import LeadFilters from "../../components/FbLeadgen/LeadFilters";
import useStages from "../../Hooks/useStages";
import useProjects from "../../Hooks/useProjects";
import useFbForms from "../../Hooks/useFbForms";
import useFbPages from "../../Hooks/useFbPages";
import LeadAssignModal from "../../Modal/Leads/LeadAssignModal";
import LeadImportModal from "../../Modal/Leads/LeadImportModal";
import CreateLeadModal from "../../Modal/Leads/CreateLeadModal";
import useLeads from "../../Hooks/useLeads";
import { toast } from "react-toastify";
import { syncLeadsNowApi } from "../../apiServices/leadServices";
import { exportLeadsApi, archiveLeadsBulkApi, unarchiveLeadsBulkApi } from "../../apiServices/genericLeadServices";
import MobileLeadCards from "../../components/FbLeadgen/MobileLeadCards";
import AppLoader from "../../components/Common/AppLoader";
import DebouncedSearchInput from "../../components/Common/DebouncedSearchInput";
import useUsersWithLeads from "../../Hooks/useUsersWithLeads";
import usePermission from "../../Hooks/usePermission";
import { getSourceBadgeInfo } from "../../utils/leadSourceHelper";

const DEFAULT_FILTERS = {
  search: "",
  assignedTo: "",
  source: "all",
  page: 1,
  limit: 20,
  stage: "",
  projectId: "",
  formId: "",
  pageId: "",
  sortBy: "createdTime",
  sortOrder: "desc",
  isArchived: false,
};

const FbLeads = () => {
  const [filters, setFilters] = useState(() => {
    try {
      const saved = sessionStorage.getItem("all_leads_filters");
      if (saved) {
        return { ...DEFAULT_FILTERS, ...JSON.parse(saved) };
      }
    } catch (e) {
      console.error("Failed to load saved leads filters", e);
    }
    return DEFAULT_FILTERS;
  });

  useEffect(() => {
    try {
      sessionStorage.setItem("all_leads_filters", JSON.stringify(filters));
    } catch (e) {
      console.error("Failed to save leads filters", e);
    }
  }, [filters]);

  const navigate = useNavigate();
  const { syncLeadsNow, syncLoading } = useLeads();
  const [syncing,setSyncing]=useState(false);
  const [refreshKey,setRefreshKey]=useState(0);
  const [importModal, setImportModal] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const { hasPermission } = usePermission();
  const { users=[] } = useUsersWithLeads();
  const { leads, loading, pagination } =  useFbLeads({ ...filters, refreshKey });
  const [filterModal,setFilterModal]=useState(false);
  const [selectedLeads,setSelectedLeads]=useState([]);
  const [assignModal,setAssignModal]=useState(false);
  const { stages=[] } = useStages({
    limit:1000,
  });
  const { projects=[] } = useProjects({
    limit:1000,
  });

  const handleArchiveLeads = async () => {
    if (!selectedLeads || selectedLeads.length === 0) {
      return toast.warning("Please select at least one lead to archive.");
    }
    try {
      setArchiving(true);
      const res = await archiveLeadsBulkApi({ leads: selectedLeads });
      toast.success(res?.message || `${selectedLeads.length} lead(s) archived successfully!`);
      setSelectedLeads([]);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.message || "Failed to archive leads.");
    } finally {
      setArchiving(false);
    }
  };

  const handleUnarchiveLeads = async () => {
    if (!selectedLeads || selectedLeads.length === 0) {
      return toast.warning("Please select at least one lead to restore.");
    }
    try {
      setArchiving(true);
      const res = await unarchiveLeadsBulkApi({ leads: selectedLeads });
      toast.success(res?.message || `${selectedLeads.length} lead(s) restored successfully!`);
      setSelectedLeads([]);
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.message || "Failed to restore leads.");
    } finally {
      setArchiving(false);
    }
  };

  const handleExportLeads = async (exportAllFiltered = false) => {
    try {
      setExporting(true);
      const payload = { ...filters };
      if (!exportAllFiltered && selectedLeads && selectedLeads.length > 0) {
        payload.leads = selectedLeads;
      } else {
        delete payload.leads;
      }

      const blob = await exportLeadsApi(payload);
      const url = window.URL.createObjectURL(new Blob([blob]));
      const link = document.createElement("a");
      link.href = url;
      const countLabel = payload.leads ? `${payload.leads.length}_selected` : `${pagination?.total || "all"}_filtered`;
      link.setAttribute("download", `leads_export_${countLabel}_${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      const exportedCount = payload.leads ? payload.leads.length : (pagination?.total || "All matching");
      toast.success(`${exportedCount} lead(s) exported successfully!`);
    } catch (err) {
      toast.error(err?.message || "Failed to export leads.");
    } finally {
      setExporting(false);
    }
  };
  const formatDateTime = (date) => {
    if (!date) return { date: "-", time: "-" };

    const d = new Date(date);

    return {
      date: d.toLocaleDateString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }),
      time: d.toLocaleTimeString("en-IN", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    };
  };
  const { forms=[] } = useFbForms({
    limit:1000,
  });
  const [isMobile,setIsMobile]=useState(window.innerWidth<=768);
  // console.log("screen width",window.innerWidth,isMobile);
  useEffect(()=>{
    const handleResize=()=>setIsMobile(window.innerWidth<=768);

    window.addEventListener("resize",handleResize);

    return ()=>window.removeEventListener("resize",handleResize);
  },[]);
  const { pages=[] } = useFbPages();
  // 🔹 HANDLE CHANGE (NO API CALL HERE)
  const handleChange = (name, value) => {
    setFilters((prev) => ({
      ...prev,
      [name]: value,
      page: 1,
    }));
  };
  const getLeadDetailsRoute = (lead) => `/lead-details/${lead.source}/${lead._id}`;

  const handleLeadClick = (lead) => {
    try {
      const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
      sessionStorage.setItem("all_leads_scroll_pos", scrollY.toString());
      sessionStorage.setItem("last_viewed_lead_id", lead._id);
    } catch (e) {}
    navigate(getLeadDetailsRoute(lead));
  };

  useLayoutEffect(() => {
    try {
      const savedPos = sessionStorage.getItem("all_leads_scroll_pos");
      const lastViewedId = sessionStorage.getItem("last_viewed_lead_id");

      const doScroll = () => {
        let scrolledByElement = false;
        if (lastViewedId) {
          const el = document.getElementById(`lead-card-${lastViewedId}`);
          if (el) {
            el.scrollIntoView({ block: "center", behavior: "instant" });
            scrolledByElement = true;
          }
        }
        if (!scrolledByElement && savedPos !== null) {
          const scrollY = parseInt(savedPos, 10);
          window.scrollTo(0, scrollY);
          document.documentElement.scrollTop = scrollY;
          document.body.scrollTop = scrollY;
          const mainContent = document.querySelector(".main-content") || document.querySelector("#layout-wrapper");
          if (mainContent) mainContent.scrollTop = scrollY;
        }
      };

      doScroll();
      requestAnimationFrame(doScroll);
      setTimeout(doScroll, 50);
      setTimeout(doScroll, 150);
      setTimeout(doScroll, 300);

      setTimeout(() => {
        try {
          sessionStorage.removeItem("all_leads_scroll_pos");
          sessionStorage.removeItem("last_viewed_lead_id");
        } catch (e) {}
      }, 500);
    } catch (e) {}
  }, [leads]);

  // 🔹 RESET
  const handleReset = () => {
    try {
      sessionStorage.removeItem("all_leads_filters");
    } catch (e) {}
    setFilters(DEFAULT_FILTERS);
    setRefreshKey(prev => prev + 1);
  };

  const normalizeArray = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === "string") return val.split(",").map((s) => s.trim()).filter(Boolean);
    return [];
  };

  const getActiveFiltersList = () => {
    const list = [];
    if (filters.search) {
      list.push({ key: "search", label: `Search: "${filters.search}"` });
    }
    if (filters.source && filters.source !== "all") {
      const srcObj = [
        { label: "Facebook", value: "facebook" },
        { label: "Google", value: "google" },
        { label: "Generic / Import", value: "generic" },
        { label: "Ai calling", value: "ai_calling" },
        { label: "Ivr calling", value: "ivr_calling" },
        { label: "RCS msg", value: "rcs_msg" },
        { label: "Whatsapp", value: "whatsapp" },
      ].find((s) => s.value === filters.source);
      list.push({ key: "source", label: `Source: ${srcObj?.label || filters.source}` });
    }

    const selectedStages = normalizeArray(filters.stage);
    selectedStages.forEach((stgId) => {
      const stg = stages.find((s) => s._id === stgId);
      list.push({ key: "stage", valId: stgId, label: `Stage: ${stg?.name || stgId}` });
    });

    const selectedProjects = normalizeArray(filters.projectId);
    selectedProjects.forEach((prjId) => {
      const prj = projects.find((p) => p._id === prjId);
      list.push({ key: "projectId", valId: prjId, label: `Project: ${prj?.projectName || prjId}` });
    });

    const selectedUsers = normalizeArray(filters.assignedTo);
    selectedUsers.forEach((usrId) => {
      const usr = users.find((u) => u._id === usrId);
      const usrName = usr ? `${usr.firstName || ""} ${usr.lastName || ""}`.trim() : usrId;
      list.push({ key: "assignedTo", valId: usrId, label: `Assigned: ${usrName}` });
    });

    const selectedForms = normalizeArray(filters.formId);
    selectedForms.forEach((frmId) => {
      const frm = forms.find((f) => f.formId === frmId);
      list.push({ key: "formId", valId: frmId, label: `Form: ${frm?.formName || frmId}` });
    });

    const selectedPages = normalizeArray(filters.pageId);
    selectedPages.forEach((pgId) => {
      const pg = pages.find((p) => p.refId === pgId);
      list.push({ key: "pageId", valId: pgId, label: `Page: ${pg?.name || pgId}` });
    });

    return list;
  };

  const activeFiltersList = getActiveFiltersList();

  const handleRemoveSingleFilter = (key, valId) => {
    setFilters((prev) => {
      if (valId) {
        const norm = normalizeArray(prev[key]);
        const remaining = norm.filter((v) => v !== valId);
        return {
          ...prev,
          [key]: remaining.length > 0 ? remaining : "",
          page: 1,
        };
      }
      const defaultVal = key === "source" ? "all" : (key === "limit" ? 20 : (key === "isArchived" ? false : ""));
      return {
        ...prev,
        [key]: defaultVal,
        page: 1,
      };
    });
  };
  // 🔹 Clear selected leads when pagination or filters change
  useEffect(() => {
    setSelectedLeads([]);
  }, [filters.page, filters.limit, filters.source, filters.search, JSON.stringify(filters.stage), JSON.stringify(filters.projectId), JSON.stringify(filters.assignedTo), JSON.stringify(filters.formId), JSON.stringify(filters.pageId)]);

  const handleLeadSelect = (lead) => {
    setSelectedLeads((prev) => {
      const exists = prev.some((item) => item.id === lead._id);

      if (exists) {
        return prev.filter((item) => item.id !== lead._id);
      }

      return [
        ...prev,
        {
          id: lead._id,
          source: lead.source,
        },
      ];
    });
  };

  const isAllSelected =
    leads.length > 0 &&
    leads.every((lead) =>
      selectedLeads.some((l) => l.id === lead._id)
    );

  const handleSelectAll = () => {
    if (isAllSelected) {
      const currentIds = new Set(leads.map((l) => l._id));
      setSelectedLeads((prev) => prev.filter((item) => !currentIds.has(item.id)));
    } else {
      setSelectedLeads((prev) => {
        const existingIds = new Set(prev.map((item) => item.id));
        const newSelections = leads
          .filter((l) => !existingIds.has(l._id))
          .map((l) => ({ id: l._id, source: l.source }));
        return [...prev, ...newSelections];
      });
    }
  };


const handleSyncLeads=async()=>{
  if(syncing){
    return toast.warning( "Lead sync already in progress" );
  }
  try{
    setSyncing(true);
    const response= await syncLeadsNowApi();
    toast.success( response?.message || "Lead synced successfully" );
    setRefreshKey(prev=>prev+1);
  }catch(error){
    toast.error( error?.error || "Failed to start lead sync" );
  }finally{
    setSyncing(false);
  }
};
  return (
    <div className="page-content">
      <Breadcrumbs title="Leads" breadcrumbItem="All Leads" />

      {/* 🔥 FILTER BAR */}
    <div className="card p-3 mb-3">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
        {/* LEFT */}
        <div style={{minWidth:"260px",maxWidth:"400px",width:"100%"}}>
          <div className="position-relative">
            <i className="mdi mdi-magnify" style={{ position:"absolute", top:"10px", left:"12px", fontSize:"16px", color:"#74788d", }} />
            <DebouncedSearchInput placeholder="Search name / email / phone..." value={filters.search} style={{paddingLeft:"35px"}} onChange={(val)=> handleChange("search", val) } delay={300} />
          </div>
        </div>
        {/* RIGHT */}
        <div className="d-flex align-items-center gap-2 flex-wrap">
          {/* Active / Archived View Switcher */}
          <div className="btn-group me-1" role="group">
            <Button
              color={!filters.isArchived ? "primary" : "light"}
              size="sm"
              className="fw-semibold"
              onClick={() => handleChange("isArchived", false)}
            >
              Active Leads <span className={`badge ${!filters.isArchived ? "bg-white text-primary" : "bg-primary text-white"} ms-1`}>{pagination?.activeCount || 0}</span>
            </Button>
            <Button
              color={filters.isArchived ? "warning" : "light"}
              size="sm"
              className="fw-semibold"
              onClick={() => handleChange("isArchived", true)}
            >
              📦 Archived Leads <span className={`badge ${filters.isArchived ? "bg-dark text-warning" : "bg-warning text-dark"} ms-1`}>{pagination?.archivedCount || 0}</span>
            </Button>
          </div>

          <Input type="select" value={filters.limit} style={{width:"95px"}} onChange={(e)=> handleChange("limit",e.target.value) } >
            {[10, 20, 50, 100, 250, 500, 1000, "all"].map((v)=>(
              <option key={v} value={v}> {v === "all" ? "All" : v} </option>
            ))}
          </Input>
          <Button
            color={activeFiltersList.length > 0 ? "primary" : "light"}
            onClick={() => setFilterModal(true)}
            title="Filter Leads"
            className="d-flex align-items-center gap-1"
          >
            <i className="mdi mdi-filter-variant" />
            {activeFiltersList.length > 0 && (
              <span className="badge bg-white text-primary rounded-circle" style={{ fontSize: "11px" }}>
                {activeFiltersList.length}
              </span>
            )}
          </Button>

          <Button
            color="success"
            onClick={() => setCreateModal(true)}
            className="px-3 fw-semibold d-flex align-items-center gap-1"
            title="Create Lead"
          >
            <i className="mdi mdi-plus" />
            Create Lead
          </Button>

          {/* SINGLE ACTIONS DROPDOWN */}
          <UncontrolledDropdown>
            <DropdownToggle
              color={selectedLeads.length > 0 ? "primary" : "secondary"}
              outline={selectedLeads.length === 0}
              className="px-3 fw-semibold d-flex align-items-center gap-1"
            >
              <i className="mdi mdi-lightning-bolt me-1" />
              Actions {selectedLeads.length > 0 ? `(${selectedLeads.length})` : ""}
              <i className="mdi mdi-chevron-down ms-1" />
            </DropdownToggle>
            <DropdownMenu end style={{ minWidth: "200px" }}>
              {!filters.isArchived && (
                <DropdownItem
                  disabled={!selectedLeads.length}
                  onClick={() => setAssignModal(true)}
                >
                  <i className="mdi mdi-account-switch me-2 text-success" />
                  Assign Selected {selectedLeads.length > 0 ? `(${selectedLeads.length})` : ""}
                </DropdownItem>
              )}

              {!filters.isArchived ? (
                <DropdownItem
                  disabled={!selectedLeads.length || archiving}
                  onClick={handleArchiveLeads}
                >
                  <i className="mdi mdi-archive-arrow-down me-2 text-danger" />
                  Archive Selected {selectedLeads.length > 0 ? `(${selectedLeads.length})` : ""}
                </DropdownItem>
              ) : (
                <DropdownItem
                  disabled={!selectedLeads.length || archiving}
                  onClick={handleUnarchiveLeads}
                >
                  <i className="mdi mdi-archive-arrow-up me-2 text-success" />
                  Restore Selected {selectedLeads.length > 0 ? `(${selectedLeads.length})` : ""}
                </DropdownItem>
              )}

              <DropdownItem divider />

              {hasPermission("generic_leads.import") && (
                <DropdownItem onClick={() => setImportModal(true)}>
                  <i className="mdi mdi-upload me-2 text-info" />
                  Import Leads
                </DropdownItem>
              )}

              {hasPermission("generic_leads.export") && (
                <>
                  {selectedLeads.length > 0 ? (
                    <>
                      <DropdownItem disabled={exporting} onClick={() => handleExportLeads(false)}>
                        <i className="mdi mdi-download me-2 text-primary" />
                        Export Selected Leads ({selectedLeads.length})
                      </DropdownItem>
                      <DropdownItem disabled={exporting || !pagination?.total} onClick={() => handleExportLeads(true)}>
                        <i className="mdi mdi-download-outline me-2 text-info" />
                        Export All Filtered Leads ({pagination?.total || 0})
                      </DropdownItem>
                    </>
                  ) : (
                    <DropdownItem disabled={exporting || !pagination?.total} onClick={() => handleExportLeads(true)}>
                      <i className="mdi mdi-download me-2 text-primary" />
                      Export Filtered Leads ({pagination?.total || 0})
                    </DropdownItem>
                  )}
                </>
              )}

              {JSON.parse(localStorage.getItem("authUser"))?.response?.role === "super_admin" && !syncing && (
                <>
                  <DropdownItem divider />
                  <DropdownItem onClick={handleSyncLeads}>
                    <i className="mdi mdi-sync me-2 text-warning" />
                    Sync Now
                  </DropdownItem>
                </>
              )}
            </DropdownMenu>
          </UncontrolledDropdown>

          <Button color="light" onClick={handleReset} title="Reset & Refresh">
            <i className="mdi mdi-refresh"/>
          </Button>
        </div>
      </div>
    </div>

      {/* 🔥 ACTIVE APPLIED FILTERS CHIPS BAR */}
      {activeFiltersList.length > 0 && (
        <div className="card p-2 mb-3 bg-light border-0 shadow-sm">
          <div className="d-flex align-items-center flex-wrap gap-2">
            <div className="fw-semibold text-muted small me-1 d-flex align-items-center gap-1">
              <i className="mdi mdi-filter-check-outline text-primary fs-6" />
              Applied Filters:
            </div>

            {activeFiltersList.map((item) => (
              <span
                key={item.key + (item.valId || "")}
                className="badge bg-white text-primary border border-primary-subtle px-3 py-2 rounded-pill d-inline-flex align-items-center gap-2 shadow-sm"
                style={{ fontSize: "12px", fontWeight: "600" }}
              >
                {item.label}
                <i
                  className="mdi mdi-close-circle cursor-pointer text-danger opacity-75"
                  style={{ fontSize: "14px", cursor: "pointer" }}
                  onClick={() => handleRemoveSingleFilter(item.key, item.valId)}
                  title={`Remove ${item.label}`}
                />
              </span>
            ))}

            <Button
              color="link"
              size="sm"
              className="text-danger text-decoration-none p-0 ms-2 fw-semibold small d-flex align-items-center gap-1"
              onClick={handleReset}
            >
              <i className="mdi mdi-trash-can-outline" /> Clear All
            </Button>
          </div>
        </div>
      )}

      {/* 🔥 TABLE */}
      <div className="card p-3">
        {
          isMobile ? (
            <MobileLeadCards
              leads={leads}
              loading={loading}
              navigate={navigate}
              onLeadClick={handleLeadClick}
              selectable={true}
              selectedLeadIds={selectedLeads.map((lead) => lead.id)}
              handleLeadSelect={handleLeadSelect}
              showAssignedUser={true}
              showProject={true}
              showCheckbox={true}
            />
          ) : (
            <div className="table-responsive">
              <Table className="table align-middle table-hover" style={{fontSize:"12px"}}>
                <thead className="table-light">
                  <tr>
                    <th className="lead-checkbox-column">
                      <label
                        className={`crm-checkbox ${isAllSelected ? "active" : ""}`}
                        onClick={(e)=>{
                          e.stopPropagation();
                          handleSelectAll();
                        }}
                      >
                        <span />
                      </label>
                    </th>
                    <th>Name</th>
                    <th>Contact</th>
                    {/* <th>Phone</th> */}
                    <th>Project</th>
                    <th>Form</th>
                    <th>Stage</th>
                    <th>Remark</th>
                    <th>Came Date</th>
                    <th>Added Date</th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="text-center py-5" style={{ minHeight: "450px" }}>
                        <div style={{ minHeight: "350px" }} className="d-flex align-items-center justify-content-center">
                          <AppLoader />
                        </div>
                      </td>
                    </tr>
                  ) : leads.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="text-center">
                        No Leads Found
                      </td>
                    </tr>
                  ) : (
                    leads.map((l) => (
                      <tr key={l._id} id={`lead-card-${l._id}`} style={{ cursor:"pointer", }} onClick={() => handleLeadClick(l)}>
                        <td
                            className="lead-checkbox-column"
                          
                          >
                            <label
                              className={`crm-checkbox ${
                                selectedLeads.some((lead) => lead.id === l._id) ? "active" : ""
                              }`}
                              onClick={(e)=>{
                                e.stopPropagation();
                                handleLeadSelect(l);
                              }}
                            >
                              <span />
                            </label>
                          </td>
                        <td>{l.name || "-"}</td>
                        <td>{l.email || "-"} <br/> {l.phone || "-"}</td>
                        <td>{l.projectName || "-"}
                          <br/>
                          {
                            l.assignedTo &&
                            l.assignedTo !== "-" && (
                              <>
                                <br/>
                                <small>
                                  <i className="mdi mdi-account-outline me-1 text-primary"></i>

                                  <Link
                                    to={`/user-leads-page/${l.assignedToId}`}
                                    className="text-primary text-decoration-none"
                                    title={l.assignedToEmail ? `${l.assignedTo} (${l.assignedToEmail})` : l.assignedTo}
                                    onClick={(e)=>e.stopPropagation()}
                                  >
                                    {l.assignedTo}
                                  </Link>
                                </small>
                              </>
                            )
                          }
                        </td>
                        <td>
                          {(() => {
                            const badgeInfo = getSourceBadgeInfo(l.source);
                            const isFacebook = badgeInfo.label === "Facebook";
                            const isGoogle = badgeInfo.label === "Google";

                            return (
                              <>
                                <div className={`d-flex align-items-center gap-1 fw-semibold ${badgeInfo.textColor || "text-info"} mb-1`}>
                                  <i className={badgeInfo.icon} style={{ fontSize: "15px" }} />
                                  <span>{badgeInfo.label}</span>
                                </div>

                                {isFacebook && (
                                  <>
                                    <div>{l.formName || "-"}</div>
                                    {l.pageName && l.pageName !== "-" && (
                                      <span className="badge bg-primary mt-1">
                                        {l.pageName}
                                      </span>
                                    )}
                                  </>
                                )}

                                {!isFacebook && !isGoogle && l.formName && l.formName !== "-" && (
                                  <span className="badge bg-soft-info text-info mt-1">
                                    {l.formName}
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </td>
                        <td> <span className="badge bg-primary"> {l.stage} </span> </td>
                        <td> <span className="badge bg-warning text-dark"> {l.remark || "-"} </span> </td>
                        <td style={{ minWidth: "140px" }} > {l.createdTime ? (() => { const dt = formatDateTime(l.createdTime); return ( <> <div className="fw-semibold">{dt.date}</div> <small className="text-muted"> <i className="mdi mdi-clock-outline me-1" /> {dt.time} </small> </> ); })() : "-"} </td>
                        <td style={{ minWidth: "140px" }} > {l.dbCreateTime ? (() => { const dt = formatDateTime(l.dbCreateTime); return ( <> <div className="fw-semibold">{dt.date}</div> <small className="text-muted"> <i className="mdi mdi-clock-outline me-1" /> {dt.time} </small> </> ); })() : "-"} </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )
        }
        <LeadFilters
          isOpen={filterModal}
          toggle={()=>setFilterModal(false)}
          filters={filters}
          setFilters={setFilters}
          stages={stages}
          projects={projects}
          forms={forms}
          pages={pages}
          users={users}
        />
        {/* 🔥 PAGINATION */}
        <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
          <Button
            size="sm"
            color="light"
            disabled={pagination.page <= 1}
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                page: prev.page - 1,
              }))
            }
          >
            <i className="mdi mdi-chevron-left" />
          </Button>

          <span className="small">
            Page {pagination.page} / {pagination.totalPages}
          </span>

          <Button
            size="sm"
            color="light"
            disabled={
              pagination.page >= pagination.totalPages
            }
            onClick={() =>
              setFilters((prev) => ({
                ...prev,
                page: prev.page + 1,
              }))
            }
          >
            <i className="mdi mdi-chevron-right" />
          </Button>
        </div>
      </div>
      <LeadAssignModal
        isOpen={assignModal}
        toggle={()=>setAssignModal(false)}
        leads={selectedLeads}
        onSuccess={()=>{
          setSelectedLeads([]);
          setRefreshKey(prev=>prev+1);
        }}
      />
      <LeadImportModal
        isOpen={importModal}
        toggle={() => setImportModal(false)}
        onSuccess={() => setRefreshKey((prev) => prev + 1)}
      />
      <CreateLeadModal
        isOpen={createModal}
        toggle={() => setCreateModal(false)}
        onSuccess={() => setRefreshKey((prev) => prev + 1)}
      />
    </div>
  );
};

export default FbLeads;